import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 12,
  onPageChange,
  onJumpToPage,
  showQuickJump = true,
  showBackToTop = true,
  className = ""
}) => {
  const { t } = useTranslation()
  const [jumpPage, setJumpPage] = useState('')

  // 生成页码按钮数组
  const getPageNumbers = () => {
    const pages = []
    const showPages = 5 // 显示的页码数量
    const halfShow = Math.floor(showPages / 2)

    let startPage = Math.max(1, currentPage - halfShow)
    let endPage = Math.min(totalPages, currentPage + halfShow)

    // 调整边界
    if (endPage - startPage + 1 < showPages) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + showPages - 1)
      } else {
        startPage = Math.max(1, endPage - showPages + 1)
      }
    }

    // 添加第一页
    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) {
        pages.push('...')
      }
    }

    // 添加中间页
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    // 添加最后一页
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('...')
      }
      pages.push(totalPages)
    }

    return pages
  }

  const handlePageClick = (page) => {
    if (typeof page === 'number' && page !== currentPage && onPageChange) {
      onPageChange(page)
    }
  }

  const handlePrevious = () => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1)
    }
  }

  const handleJumpSubmit = (e) => {
    e.preventDefault()
    const page = parseInt(jumpPage)
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      if (onJumpToPage) {
        onJumpToPage(page)
      } else if (onPageChange) {
        onPageChange(page)
      }
    }
    setJumpPage('')
  }

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (totalPages <= 1) {
    return null
  }

  const pages = getPageNumbers()

  return (
    <div className={`pagination-container bg-white py-6 ${className}`}>
      {/* 分页信息 */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="pagination-info text-sm text-gray-600">
          {t('products.pagination.pageInfo', { current: currentPage, total: totalPages })} · 
          {t('products.pagination.totalItems', { count: totalItems })}
        </div>

        {/* 分页控件 */}
        <div className="pagination-controls flex items-center gap-2">
          {/* 上一页 */}
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="pagination-btn px-3 py-2 text-sm border rounded-lg
                     disabled:opacity-50 disabled:cursor-not-allowed
                     enabled:hover:bg-gray-50 enabled:hover:border-gray-400"
            title={t('products.pagination.previous')}
          >
            ◀
          </button>

          {/* 页码按钮 */}
          <div className="flex items-center gap-1">
            {pages.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-2 text-sm text-gray-400">...</span>
                ) : (
                  <button
                    onClick={() => handlePageClick(page)}
                    className={`pagination-btn px-3 py-2 text-sm border rounded-lg
                               transition-colors duration-200
                               ${page === currentPage
                                 ? 'bg-primary-600 border-primary-600 text-white'
                                 : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'}`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* 下一页 */}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="pagination-btn px-3 py-2 text-sm border rounded-lg
                     disabled:opacity-50 disabled:cursor-not-allowed
                     enabled:hover:bg-gray-50 enabled:hover:border-gray-400"
            title={t('products.pagination.next')}
          >
            ▶
          </button>
        </div>

        {/* 快速跳转和回到顶部 */}
        <div className="pagination-extras flex items-center gap-4">
          {/* 快速跳转 */}
          {showQuickJump && (
            <form onSubmit={handleJumpSubmit} className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('products.pagination.quickJump')}</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={t('products.pagination.jumpToPage')}
              />
              <span className="text-sm text-gray-600">{t('products.pagination.jumpToPage')}</span>
              <button
                type="submit"
                className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg
                         hover:bg-primary-700 transition-colors duration-200"
              >
                {t('products.pagination.jump')}
              </button>
            </form>
          )}

          {/* 回到顶部 */}
          {showBackToTop && (
            <button
              onClick={handleBackToTop}
              className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600
                       hover:text-primary-600 transition-colors duration-200"
              title={t('products.pagination.backToTop')}
            >
              {t('products.pagination.backToTop')} ⬆
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Pagination 